import { useCallback, useMemo, useEffect } from 'react';
import { useAppSelector } from '../../../redux/Store';
import {
  MobileStateMediaItem,
  SaveDataMediaItem,
  AuditStateMediaItem,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { useDispatch } from 'react-redux';
import {
  removeAdditionalLabel,
  deleteMediaLabel,
  getAdditionalPhotoLabels,
} from '../../../redux/reducer/coreScriptReducer';
import { ImageMetadata } from '../../../types/ui';
import { OtherHouses } from '@mui/icons-material';

const useMediaHelper = () => {
  const dispatch = useDispatch();
  const { saveData, state } = useAppSelector(
    (state) => state.coreScript.coreScript
  );

  const uploadedImageDetails = useAppSelector(
    (state) => state.gallery.uploadedImageDetails
  );

  const additionalPhotoImages = useAppSelector(getAdditionalPhotoLabels); //Addition Labels with attached Images

  const medias = useMemo(
    () => (saveData.Media ? Object.values(saveData.Media) : []),
    [saveData]
  );

  const createMediaByLabelCache = () => {
    const cache: Record<string, SaveDataMediaItem[]> = {};
    for (const media of medias) {
      for (const label of media.Label) {
        if (!cache[label]) {
          cache[label] = [];
        }
        cache[label].push(media);
      }
    }
    return cache;
  };

  const mediaByLabelCache = useMemo(createMediaByLabelCache, [medias]);

  const getMediasByQuestion = (question: string): SaveDataMediaItem[] => {
    if (!question) return [];
    const labels = state?.Media?.ByQuestion[question] || [];
    const result: SaveDataMediaItem[] = [];
    for (const label of labels) {
      const mediaItems = mediaByLabelCache[label];
      if (mediaItems) {
        result.push(...mediaItems);
      }
    }
    return result;
  };

  const getMediasByLabel = (label: string): SaveDataMediaItem[] => {
    return mediaByLabelCache[label] || [];
  };

  const removeMediasByLabel = (label: string, removableIds?: string[]) => {
    if (!label) {
      console.log(label, 'Label cannot be removed');
      return;
    }
    const guids =
      removableIds && removableIds?.length
        ? removableIds
        : getMediasByLabel(label).map((media) => media.Guid);
    dispatch(deleteMediaLabel({ label, guids }));
  };

  const removeMediasByQuestion = (question: string) => {
    if (!question) {
      console.log(question, 'Question cannot be removed');
      return;
    }
    const labels = state?.Media?.ByQuestion[question] || [];
    for (const label of labels) {
      removeMediasByLabel(label);
    }
  };

  const imageMap = useMemo(() => {
    const map = new Map<string, ImageMetadata>();
    for (const image of uploadedImageDetails) {
      let active = false;
      const label = saveData?.Media[image.guid]?.Label[0];

      for (const key in state?.Media?.List) {
        const mediaIitem = state?.Media?.List[key] as AuditStateMediaItem;
        if (mediaIitem.Images != undefined) {
          const mediaIitemguid = mediaIitem.Images[image.guid];
          if (mediaIitem && mediaIitemguid) {
            active = true;
          }
        }
      }

      map.set(image.guid, {
        ...image,
        label: label,
        active: active,
      });
    }
    return map;
  }, [uploadedImageDetails, state, saveData.Media]);

  useEffect(() => {
    // Collect all (Label, guid) pairs that need removal

    const removals: { label: string; guids: string[] }[] = [];

    for (const additionalPhotoImage of additionalPhotoImages) {
      if (Array.isArray(additionalPhotoImage.Images)) {
        for (const img of additionalPhotoImage.Images) {
          const hasImage = imageMap.get(img.guid);
          if (hasImage?.active) {
            removals.push({
              label: additionalPhotoImage.Label,
              guids: [img.guid],
            });
          }
        }
      }
    }

    // Dispatch removals (individually or batched)
    for (const removal of removals) {
      dispatch(removeAdditionalLabel(removal));
    }
  }, [imageMap, additionalPhotoImages, dispatch]);

  const getUnlabeledImages = useCallback((): ImageMetadata[] => {
    const isImageInAdditionalPhotos = (guid: string): boolean => {
      return additionalPhotoImages.some(
        (item: any) =>
          Array.isArray(item.Images) &&
          item.Images.some((img: any) => img.guid === guid)
      );
    };

    const unlabeledImages: ImageMetadata[] = [];
    // eslint-disable-next-line unicorn/no-array-for-each
    imageMap.forEach((image) => {
      if (
        (!image.label || !image.active) &&
        image.imageType === '5' && // Only consider images of type '5' Photo can be used as unlabeled
        !isImageInAdditionalPhotos(image.guid)
      ) {
        unlabeledImages.push(image);
      }
    });
    return unlabeledImages;
  }, [imageMap, additionalPhotoImages]);

  const getAllLabeledImages = useCallback((): ImageMetadata[] => {
    const labeledImages: ImageMetadata[] = [];
    // eslint-disable-next-line unicorn/no-array-for-each
    imageMap.forEach((image) => {
      if (image.label) {
        labeledImages.push(image);
      }
    });
    return labeledImages;
  }, [imageMap]);

  const getAllImagesByLabel = (label: string) => {
    if (!label) {
      console.log(label, 'Cannot get images for this label');
      return [];
    }
    const mediaItems = mediaByLabelCache[label] || [];
    const result: ImageMetadata[] = [];
    for (const media of mediaItems) {
      const metadata = imageMap.get(media.Guid);
      if (metadata) {
        result.push(metadata);
      }
    }
    return result;
  };

  const getAllImagesByQuestion = (question: string) => {
    if (!question) {
      console.log(question, 'Cannot get images for this question');
      return [];
    }
    const mediaItems = getMediasByQuestion(question);
    const result: ImageMetadata[] = [];
    for (const media of mediaItems) {
      const metadata = imageMap.get(media.Guid);
      if (metadata) {
        result.push(metadata);
      }
    }

    return result;
  };

  const getActiveLabelsForQuestion = useCallback(
    (question: string) => {
      const mediaKeys = [...new Set(state?.Media?.ByQuestion[question] || [])];
      return mediaKeys
        .map((key: string) => state?.Media.List[key])
        .filter(Boolean) // Filter out undefined keys
        .sort(
          (a: any, b: any) =>
            (a.Complete === true ? 1 : 0) - (b.Complete === true ? 1 : 0)
        );
    },
    [state]
  );

  return {
    imageMap,
    getMediasByLabel,
    removeMediasByQuestion,
    getAllImagesByQuestion,
    getAllImagesByLabel,
    getActiveLabelsForQuestion,
    removeMediasByLabel,
    getMediasByQuestion,
    getUnlabeledImages,
    getAllLabeledImages,
  };
};

export default useMediaHelper;
